import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  Check,
  Copy,
  Download,
  
  Loader2,
  Lock,
  Maximize,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Share2,
  Star,
  Trash2,
  Volume2,
  VolumeX,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { VoiceChangeCta } from "@/components/voice/VoiceChangeOption";
import { AutoVoiceChange } from "@/components/voice/AutoVoiceChange";

export const Route = createFileRoute("/_authenticated/result/$id")({
  head: () => ({
    meta: [
      { title: "生成結果 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ResultPage,
});

type Generation = {
  id: string;
  user_id: string;
  status: string;
  output_video_url: string | null;
  input_image_url: string | null;
  input_video_url: string | null;
  quality_tier: string | null;
  credits_consumed: number;
  is_favorite: boolean;
  created_at: string;
  completed_at: string | null;
  video_duration_sec: number | null;
  video_width: number | null;
  video_height: number | null;
  is_blocked?: boolean | null;
  title: string | null;
};

const QUALITY_LABEL: Record<string, string> = {
  trial: "お試し",
  standard: "スタンダード",
  high: "ハイ",
  premium: "プレミアム",
  lipsync: "リップシンク",
};

const SPEED_OPTIONS = [0.5, 1, 1.5, 2] as const;

function formatTime(sec: number) {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function ResultPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [gen, setGen] = useState<Generation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const currentUserId = userData.user?.id;

      const { data, error } = await supabase
        .from("generations")
        .select(
          "id, user_id, status, output_video_url, input_image_url, input_video_url, quality_tier, credits_consumed, is_favorite, created_at, completed_at, video_duration_sec, video_width, video_height, is_blocked, title",
        )
        .eq("id", id)
        .is("deleted_at", null)
        .maybeSingle();

      if (!active) return;
      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }
      if (!data) {
        // RLSで弾かれた or 存在しない → 所有者でなければ403扱い
        setForbidden(true);
        setLoading(false);
        return;
      }
      if (currentUserId && data.user_id !== currentUserId) {
        setForbidden(true);
        setLoading(false);
        return;
      }
      setGen(data as Generation);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const handleToggleFavorite = async () => {
    if (!gen) return;
    const next = !gen.is_favorite;
    setGen({ ...gen, is_favorite: next });
    const { error } = await supabase
      .from("generations")
      .update({ is_favorite: next })
      .eq("id", gen.id);
    if (error) {
      setGen({ ...gen, is_favorite: !next });
      toast.error("お気に入りの更新に失敗しました");
    } else {
      toast.success(next ? "お気に入りに追加しました" : "お気に入りを解除しました");
    }
  };

  const handleDelete = async () => {
    if (!gen) return;
    const { error } = await supabase
      .from("generations")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", gen.id);
    if (error) {
      toast.error("削除に失敗しました");
      return;
    }
    toast.success("削除しました");
    navigate({ to: "/history", replace: true });
  };

  const handleRetryWithSameInputs = () => {
    if (!gen) return;
    // 同じ素材で別品質を試す: /create/quality に素材をクエリで引き継ぎ
    navigate({
      to: "/create/quality",
      search: {
        image: gen.input_image_url ?? undefined,
        video: gen.input_video_url ?? undefined,
      } as never,
      replace: false,
    });
  };

  const handleMetaLoaded = useCallback(
    async (meta: { duration: number; width: number; height: number }) => {
      if (!gen) return;
      if (
        gen.video_duration_sec != null &&
        gen.video_width != null &&
        gen.video_height != null
      ) {
        return;
      }
      const patch: {
        video_duration_sec?: number;
        video_width?: number;
        video_height?: number;
      } = {};
      if (gen.video_duration_sec == null) patch.video_duration_sec = meta.duration;
      if (gen.video_width == null) patch.video_width = meta.width;
      if (gen.video_height == null) patch.video_height = meta.height;
      if (Object.keys(patch).length === 0) return;
      const { error } = await supabase
        .from("generations")
        .update(patch)
        .eq("id", gen.id);
      if (error) {
        console.warn("video meta update failed", error);
        return;
      }
      setGen((prev) => (prev ? { ...prev, ...patch } : prev));
    },
    [gen],
  );

  const handleSaveTitle = async () => {
    if (!gen) return;
    const next = titleDraft.trim().slice(0, 100);
    setSavingTitle(true);
    const { error } = await supabase
      .from("generations")
      .update({ title: next || null })
      .eq("id", gen.id);
    setSavingTitle(false);
    if (error) {
      toast.error("名前の保存に失敗しました");
      return;
    }
    setGen({ ...gen, title: next || null });
    setEditingTitle(false);
    toast.success("名前を保存しました");
  };

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  if (forbidden) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <Lock className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">403 アクセスできません</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              この動画にアクセスする権限がありません。
            </p>
            <Button asChild className="mt-6">
              <Link to="/dashboard">ダッシュボードへ</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  if (error || !gen) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">読み込みに失敗しました</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {error ?? "生成データが見つかりませんでした。"}
            </p>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-8 sm:py-10">
      <div className="mx-auto max-w-6xl">
        {editingTitle ? (
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={titleDraft}
              maxLength={100}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSaveTitle();
                if (e.key === "Escape") setEditingTitle(false);
              }}
              placeholder="動画の名前を入力"
              className="h-10 max-w-md text-xl font-bold"
            />
            <Button size="sm" onClick={handleSaveTitle} disabled={savingTitle}>
              {savingTitle ? <Loader2 className="h-4 w-4 animate-spin" /> : "保存"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingTitle(false)}>
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setTitleDraft(gen.title ?? "");
              setEditingTitle(true);
            }}
            className="group flex items-center gap-2 text-left"
            aria-label="名前を編集"
          >
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {gen.title?.trim() || "無題の動画"}
            </h1>
            <Pencil className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          </button>
        )}

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
          {/* 左: 動画プレイヤー + アクション */}
          <div className="space-y-4">
            {gen.is_blocked && (
              <Card className="border-destructive/40 bg-destructive/10 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 text-destructive" />
                  <div>
                    <p className="text-sm font-semibold text-destructive">
                      この動画は現在審査中です
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      通報を受け、内容を確認しています。確認が完了するまで再生できません。
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <div
              className="mx-auto w-full"
              style={{ maxWidth: "min(420px, calc((100vh - 260px) * 9 / 16))" }}
            >
              {gen.is_blocked ? (
                <div className="flex aspect-[9/16] items-center justify-center rounded-xl border bg-muted text-sm text-muted-foreground">
                  審査中のため再生できません
                </div>
              ) : gen.output_video_url ? (
                <CustomVideoPlayer
                  src={gen.output_video_url}
                  generationId={gen.id}
                  onMetaLoaded={handleMetaLoaded}
                />
              ) : (
                <div className="flex aspect-[9/16] items-center justify-center rounded-xl border bg-muted text-sm text-muted-foreground">
                  動画URLがまだ反映されていません
                </div>
              )}
            </div>

            {gen.output_video_url && !gen.is_blocked && (
              <ActionButtons
                gen={gen}
                onToggleFavorite={handleToggleFavorite}
                onDelete={() => setDeleteOpen(true)}
                onRetry={handleRetryWithSameInputs}
              />
            )}

            {!gen.is_blocked && (
              <AutoVoiceChange
                videoUrl={gen.output_video_url}
                className="mx-auto w-full max-w-[420px]"
              />
            )}


          </div>

          {/* 右: 生成情報 */}
          <InfoSidebar gen={gen} />
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>この動画を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              削除すると履歴・結果ページから表示されなくなります。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>削除する</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

function CustomVideoPlayer({
  src,
  generationId,
  onMetaLoaded,
}: {
  src: string;
  generationId: string;
  onMetaLoaded?: (meta: { duration: number; width: number; height: number }) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState<(typeof SPEED_OPTIONS)[number]>(1);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTime = () => setCurrent(v.currentTime);
    const onLoaded = () => {
      setDuration(v.duration || 0);
      if (onMetaLoaded && v.duration && v.videoWidth && v.videoHeight) {
        onMetaLoaded({
          duration: v.duration,
          width: v.videoWidth,
          height: v.videoHeight,
        });
      }
    };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onLoaded);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onLoaded);
    };
  }, [onMetaLoaded]);


  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play();
    else v.pause();
  };

  const onSeek = (vals: number[]) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = vals[0];
    setCurrent(vals[0]);
  };

  const onVolume = (vals: number[]) => {
    const v = videoRef.current;
    if (!v) return;
    const next = vals[0];
    v.volume = next;
    setVolume(next);
    setMuted(next === 0);
    v.muted = next === 0;
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const goFullscreen = () => {
    const c = containerRef.current;
    if (!c) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void c.requestFullscreen();
  };

  const setPlaybackRate = (r: (typeof SPEED_OPTIONS)[number]) => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = r;
    setSpeed(r);
  };

  return (
    <div
      ref={containerRef}
      className="group relative overflow-hidden rounded-xl border bg-black"
    >
      <video
        ref={videoRef}
        src={src}
        playsInline
        preload="metadata"
        onClick={togglePlay}
        className="aspect-[9/16] w-full cursor-pointer bg-black object-contain"
        data-testid={`video-${generationId}`}
      />

      {/* オーバーレイ再生ボタン */}
      {!playing && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40"
          aria-label="再生"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-black shadow-lg">
            <Play className="ml-1 h-8 w-8" fill="currentColor" />
          </span>
        </button>
      )}

      {/* コントロール */}
      <div className="absolute inset-x-0 bottom-0 space-y-2 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
        <Slider
          value={[current]}
          max={duration || 0.0001}
          step={0.1}
          onValueChange={onSeek}
          className="[&_[role=slider]]:h-3 [&_[role=slider]]:w-3"
        />
        <div className="flex items-center gap-2 text-white">
          <button
            type="button"
            onClick={togglePlay}
            className="rounded p-1 hover:bg-white/20"
            aria-label={playing ? "一時停止" : "再生"}
          >
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            className="rounded p-1 hover:bg-white/20"
            aria-label={muted ? "ミュート解除" : "ミュート"}
          >
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
          <div className="hidden w-20 sm:block">
            <Slider
              value={[muted ? 0 : volume]}
              min={0}
              max={1}
              step={0.05}
              onValueChange={onVolume}
            />
          </div>
          <span className="ml-1 text-xs tabular-nums text-white/80">
            {formatTime(current)} / {formatTime(duration)}
          </span>
          <div className="ml-auto flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="rounded px-2 py-1 text-xs font-semibold hover:bg-white/20"
                  aria-label="再生速度"
                >
                  {speed}x
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {SPEED_OPTIONS.map((r) => (
                  <DropdownMenuItem
                    key={r}
                    onSelect={() => setPlaybackRate(r)}
                    className={cn(speed === r && "font-semibold text-primary")}
                  >
                    {r}x {speed === r && <Check className="ml-auto h-4 w-4" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              onClick={goFullscreen}
              className="rounded p-1 hover:bg-white/20"
              aria-label="全画面"
            >
              <Maximize className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionButtons({
  gen,
  onToggleFavorite,
  onDelete,
  onRetry,
}: {
  gen: Generation;
  onToggleFavorite: () => void;
  onDelete: () => void;
  onRetry: () => void;
}) {
  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/result/${gen.id}`;
  }, [gen.id]);
  const shareText = "AI動画を生成しました！";

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("URLをコピーしました");
    } catch {
      toast.error("コピーに失敗しました");
    }
  };

  const openExternal = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async () => {
    if (!gen.output_video_url) return;
    const filename = `generation-${gen.id}.mp4`;
    try {
      const res = await fetch(gen.output_video_url);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      // File System Access API (Chrome/Edge) — lets user pick save location
      const w = window as unknown as {
        showSaveFilePicker?: (opts: {
          suggestedName: string;
          types: { description: string; accept: Record<string, string[]> }[];
        }) => Promise<{
          createWritable: () => Promise<{
            write: (data: Blob) => Promise<void>;
            close: () => Promise<void>;
          }>;
        }>;
      };
      if (typeof w.showSaveFilePicker === "function") {
        try {
          const handle = await w.showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: "MP4 動画",
                accept: { "video/mp4": [".mp4"] },
              },
            ],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success("ダウンロードしました");
          return;
        } catch (e) {
          // ユーザーがキャンセルした場合は何もしない
          if ((e as { name?: string })?.name === "AbortError") return;
          // それ以外は通常DLにフォールバック
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("ダウンロードに失敗しました");
    }
  };

  return (
    <div className="flex flex-wrap justify-center gap-2">
      <Button onClick={handleDownload} disabled={!gen.output_video_url}>
        <Download className="h-4 w-4" />
        ダウンロード
      </Button>

      <VoiceChangeCta variant="outline" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Share2 className="h-4 w-4" />
            共有
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem
            onSelect={() =>
              openExternal(
                `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
              )
            }
          >
            X（旧Twitter）に投稿
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openExternal("https://www.tiktok.com/upload")}>
            TikTokにアップロード
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openExternal("https://www.instagram.com/")}>
            Instagramを開く
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              openExternal(
                `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}`,
              )
            }
          >
            LINEで送る
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={copyUrl}>
            <Copy className="mr-2 h-4 w-4" />
            URLをコピー
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button variant="outline" onClick={onRetry}>
        <RefreshCw className="h-4 w-4" />
        もう一回作る
      </Button>

      <Button variant="outline" asChild>
        <Link to="/create/video" search={{ from: gen.id } as never}>
          <RefreshCw className="h-4 w-4" />
          設定を変えて再生成
        </Link>
      </Button>

      <Button variant="outline" asChild>
        <Link to="/create">
          <Plus className="h-4 w-4" />
          新規作成
        </Link>
      </Button>

      <Button
        variant={gen.is_favorite ? "default" : "outline"}
        onClick={onToggleFavorite}
      >
        <Star
          className={cn("h-4 w-4", gen.is_favorite && "fill-current")}
        />
        お気に入り
      </Button>

      <Button variant="outline" onClick={onDelete}>
        <Trash2 className="h-4 w-4" />
        削除
      </Button>
    </div>
  );
}

function InfoSidebar({ gen }: { gen: Generation }) {
  const created = new Date(gen.created_at).toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  // 解像度を 720p / 1080p / 4K のような短縮ラベルにする（不可なら WxH 表示）
  const resolutionLabel = (() => {
    const h = gen.video_height;
    if (!h) return null;
    if (h >= 2160) return "4K";
    if (h >= 1440) return "1440p";
    if (h >= 1080) return "1080p";
    if (h >= 720) return "720p";
    if (h >= 480) return "480p";
    return `${gen.video_width ?? "?"}×${h}`;
  })();
  const durationLabel =
    gen.video_duration_sec != null ? `${Math.round(gen.video_duration_sec)}秒` : null;
  const sizeLine =
    durationLabel && resolutionLabel
      ? `${durationLabel} / ${resolutionLabel}`
      : (durationLabel ?? resolutionLabel ?? "—");

  return (
    <Card className="h-fit p-5">
      <h2 className="text-base font-semibold">生成情報</h2>
      <dl className="mt-4 space-y-3 text-sm">
        <Row label="品質グレード" value={QUALITY_LABEL[gen.quality_tier ?? ""] ?? gen.quality_tier ?? "—"} />
        <Row label="生成日時" value={created} />
        <Row label="消費クレジット" value={`${gen.credits_consumed}`} />
        <Row label="動画の長さ・解像度" value={sizeLine} />
        
      </dl>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}
